import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { SalesRequest, SalesRequestDocument } from "~/types/client";

import { fetchSalesRequestDocuments } from "./salesRequestDocumentService";

/**
 * Downloads a factura along with related sales request documents (orden de compra, guía de despacho)
 * Opens each document in a new tab for download
 */
export const downloadFacturaWithDocuments = async (
  salesRequest: SalesRequest,
  clientName: string
): Promise<void> => {
  try {
    // Show loading toast
    toast({
      title: "Preparando descarga...",
      description: "Recopilando documentos relacionados",
    });

    // Fetch all related documents
    const allDocuments = await fetchSalesRequestDocuments(salesRequest.id);

    // Filter for relevant documents
    const relevantDocuments = allDocuments.filter((doc) =>
      ["factura", "orden_de_compra", "guia_despacho"].includes(doc.documentType)
    );

    if (relevantDocuments.length === 0) {
      toast({
        title: "Sin documentos",
        description: "No se encontraron documentos para descargar",
        variant: "destructive",
      });
      return;
    }

    // Download each document with a delay to avoid popup blockers
    let downloadedCount = 0;
    for (const [index, doc] of relevantDocuments.entries()) {
      setTimeout(() => {
        try {
          window.open(doc.fileUrl, "_blank");
          downloadedCount++;

          // Show completion message after last download
          if (index === relevantDocuments.length - 1) {
            setTimeout(() => {
              toast({
                title: "Descarga iniciada",
                description: `Se abrieron ${relevantDocuments.length} documentos para descarga: ${getDocumentTypeNames(relevantDocuments)}`,
              });
            }, 500);
          }
        } catch (error) {
          console.warn(`Error downloading ${doc.documentType}:`, error);
        }
      }, index * 300); // 300ms delay between each download
    }
  } catch (error) {
    console.error("Error downloading documents:", error);
    toast({
      title: "Error en descarga",
      description: "Ocurrió un error al preparar la descarga de documentos",
      variant: "destructive",
    });
  }
};

/**
 * Gets human-readable names for document types
 */
const getDocumentTypeNames = (documents: SalesRequestDocument[]): string => {
  const typeNames: Record<string, string> = {
    factura: "Factura",
    orden_de_compra: "Orden de Compra",
    guia_despacho: "Guía de Despacho",
  };

  const names = documents.map(
    (doc) => typeNames[doc.documentType] || doc.documentType
  );
  return names.join(", ");
};

/**
 * Generates appropriate file name for each document type
 */
const getDocumentFileName = (
  doc: SalesRequestDocument,
  clientName: string,
  salesRequest: SalesRequest
): string => {
  const cleanClientName = clientName.replace(/[^a-zA-Z0-9\s]/g, "").trim();
  const orderId = salesRequest.id.substring(0, 8);
  const extension = getFileExtension(doc.fileName);

  switch (doc.documentType) {
    case "factura":
      return `${cleanClientName}_${orderId}_factura${extension}`;
    case "orden_de_compra":
      return `${cleanClientName}_${orderId}_orden_compra${extension}`;
    case "guia_despacho":
      return `${cleanClientName}_${orderId}_guia_despacho${extension}`;
    default:
      return `${cleanClientName}_${orderId}_${doc.documentType}${extension}`;
  }
};

/**
 * Extracts file extension from filename
 */
const getFileExtension = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf(".");
  return lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : "";
};

/**
 * Downloads only the factura (fallback for when related documents aren't needed)
 */
export const downloadFacturaOnly = async (
  salesRequest: SalesRequest,
  clientName: string
): Promise<void> => {
  try {
    // Fetch factura document
    const { data: facturaDoc, error: facturaError } = await supabase
      .from("sales_request_documents")
      .select("*")
      .eq("sales_request_id", salesRequest.id)
      .eq("document_type", "factura")
      .single();

    if (facturaError || !facturaDoc) {
      toast({
        title: "Error",
        description: "No se encontró la factura para esta nota de venta",
        variant: "destructive",
      });
      return;
    }

    // Open factura in new tab
    window.open(facturaDoc.document_url, "_blank");
  } catch (error) {
    console.error("Error downloading factura:", error);
    toast({
      title: "Error en descarga",
      description: "Ocurrió un error al descargar la factura",
      variant: "destructive",
    });
  }
};

/**
 * Merges multiple factura PDFs into a single file and auto-triggers print dialog
 * Calls the merge-facturas Edge Function to combine PDFs server-side
 */
export const bulkPrintFacturas = async (
  salesRequestIds: string[]
): Promise<{ success: boolean }> => {
  try {
    if (salesRequestIds.length === 0) {
      return { success: false };
    }

    // Call the Edge Function to merge PDFs
    const { data, error } = await supabase.functions.invoke("merge-facturas", {
      body: { sales_request_ids: salesRequestIds },
    });

    if (error) {
      console.error("Error calling merge-facturas:", error);
      toast({
        title: "Error",
        description: "Error al combinar las facturas",
        variant: "destructive",
      });
      return { success: false };
    }

    // Create blob and use iframe to auto-print
    const blob = new Blob([data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = url;
    document.body.appendChild(iframe);

    iframe.onload = () => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    };

    toast({
      title: "Facturas listas",
      description: "Se abrió el diálogo de impresión",
    });

    return { success: true };
  } catch (error) {
    console.error("Error in bulkPrintFacturas:", error);
    toast({
      title: "Error",
      description: "Ocurrió un error al preparar las facturas",
      variant: "destructive",
    });
    return { success: false };
  }
};
